import {
  AppNotification,
  Booking,
  NotificationPreferenceKey,
  NotificationType,
  ReminderPlan,
  ReminderType,
  WaitlistEntry,
} from '../types';

const REMINDER_KEY = 'halisaha_reminder_plans';
const NOTIFICATION_KEY = 'halisaha_notifications';
const WAITLIST_KEY = 'halisaha_waitlist_entries';
const NOTIFICATION_PREFS_KEY = 'halisaha_notification_prefs_v1';

type NotificationPreferences = Record<NotificationPreferenceKey, boolean>;
type NotificationPrefsMap = Record<string, NotificationPreferences>;
type NotificationDeliveryPreferences = {
  browser: boolean;
  sound: boolean;
};
type NotificationDeliveryPrefsMap = Record<string, NotificationDeliveryPreferences>;
const NOTIFICATION_DELIVERY_PREFS_KEY = 'halisaha_notification_delivery_prefs_v1';

function safeRead<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function safeWrite<T>(key: string, value: T) {
  localStorage.setItem(key, JSON.stringify(value));
}

const DEFAULT_NOTIFICATION_PREFS: NotificationPreferences = {
  booking: true,
  waitlist: true,
  reminder: true,
  subscription: true,
};
const DEFAULT_DELIVERY_PREFS: NotificationDeliveryPreferences = {
  browser: true,
  sound: true,
};

function normalizePhone(phone?: string): string {
  return (phone || '').replace(/\D/g, '');
}

function normalizeTrPhoneForWhatsapp(phone?: string): string {
  const digits = normalizePhone(phone);
  if (!digits) return '';
  if (digits.startsWith('90') && digits.length >= 12) return digits;
  if (digits.startsWith('0') && digits.length >= 11) return `90${digits.slice(1)}`;
  if (digits.length === 10) return `90${digits}`;
  return digits;
}

function isSameTarget(
  target: { userId?: string; contactPhone?: string },
  source: { userId?: string; contactPhone?: string }
): boolean {
  if (target.userId && source.userId) return target.userId === source.userId;
  const targetPhone = normalizePhone(target.contactPhone);
  const sourcePhone = normalizePhone(source.contactPhone);
  return Boolean(targetPhone) && targetPhone === sourcePhone;
}

function getNotificationTargetKey(input: { userId?: string; contactPhone?: string }): string {
  if (input.userId) return `u:${input.userId}`;
  const phone = normalizePhone(input.contactPhone);
  if (phone) return `p:${phone}`;
  return '';
}

function readNotificationPrefsMap(): NotificationPrefsMap {
  return safeRead<NotificationPrefsMap>(NOTIFICATION_PREFS_KEY, {});
}

function writeNotificationPrefsMap(value: NotificationPrefsMap) {
  safeWrite(NOTIFICATION_PREFS_KEY, value);
}

function readNotificationDeliveryPrefsMap(): NotificationDeliveryPrefsMap {
  return safeRead<NotificationDeliveryPrefsMap>(NOTIFICATION_DELIVERY_PREFS_KEY, {});
}

function writeNotificationDeliveryPrefsMap(value: NotificationDeliveryPrefsMap) {
  safeWrite(NOTIFICATION_DELIVERY_PREFS_KEY, value);
}

function getPreferenceKeyByType(type: NotificationType): NotificationPreferenceKey {
  if (type === 'waitlist_joined' || type === 'waitlist_slot_opened') return 'waitlist';
  if (type === 'reminder_24h' || type === 'reminder_2h') return 'reminder';
  if (
    type === 'subscription_request_owner' ||
    type === 'subscription_approved_customer' ||
    type === 'subscription_activated_customer' ||
    type === 'subscription_activated_owner' ||
    type === 'subscription_cancelled_customer' ||
    type === 'subscription_cancelled_owner'
  ) {
    return 'subscription';
  }
  return 'booking';
}

function shouldSendNotification(
  target: { userId?: string; contactPhone?: string },
  type: NotificationType
): boolean {
  const key = getNotificationTargetKey(target);
  if (!key) return false;
  const map = readNotificationPrefsMap();
  const prefs = map[key] || DEFAULT_NOTIFICATION_PREFS;
  return prefs[getPreferenceKeyByType(type)] ?? true;
}

function parseBookingDateHour(date: string, hour: number): Date {
  const [y, m, d] = date.split('-').map(Number);
  const totalMinutes = Math.round(hour * 60);
  const hh = Math.floor(totalMinutes / 60);
  const mm = totalMinutes % 60;
  return new Date(y, (m || 1) - 1, d || 1, hh, mm, 0, 0);
}

function formatHour(value: number): string {
  const totalMinutes = Math.round(value * 60);
  const hh = Math.floor(totalMinutes / 60);
  const mm = totalMinutes % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function createNotification(input: Omit<AppNotification, 'id' | 'createdAt' | 'read'>) {
  if (!shouldSendNotification({ userId: input.userId, contactPhone: input.contactPhone }, input.type)) {
    return;
  }
  const current = safeRead<AppNotification[]>(NOTIFICATION_KEY, []);
  const next: AppNotification = {
    ...input,
    id: `notification-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: new Date().toISOString(),
    read: false,
  };
  safeWrite(NOTIFICATION_KEY, [next, ...current]);
}

export function getNotificationPreferences(input: {
  userId?: string;
  contactPhone?: string;
}): NotificationPreferences {
  const targetKey = getNotificationTargetKey(input);
  if (!targetKey) return { ...DEFAULT_NOTIFICATION_PREFS };
  const map = readNotificationPrefsMap();
  return { ...DEFAULT_NOTIFICATION_PREFS, ...(map[targetKey] || {}) };
}

export function setNotificationPreference(input: {
  userId?: string;
  contactPhone?: string;
  key: NotificationPreferenceKey;
  enabled: boolean;
}): NotificationPreferences {
  const targetKey = getNotificationTargetKey(input);
  if (!targetKey) return { ...DEFAULT_NOTIFICATION_PREFS };
  const map = readNotificationPrefsMap();
  const current = { ...DEFAULT_NOTIFICATION_PREFS, ...(map[targetKey] || {}) };
  current[input.key] = input.enabled;
  map[targetKey] = current;
  writeNotificationPrefsMap(map);
  return current;
}

export function getNotificationDeliveryPreferences(input: {
  userId?: string;
  contactPhone?: string;
}): NotificationDeliveryPreferences {
  const targetKey = getNotificationTargetKey(input);
  if (!targetKey) return { ...DEFAULT_DELIVERY_PREFS };
  const map = readNotificationDeliveryPrefsMap();
  return { ...DEFAULT_DELIVERY_PREFS, ...(map[targetKey] || {}) };
}

export function setNotificationDeliveryPreference(input: {
  userId?: string;
  contactPhone?: string;
  key: keyof NotificationDeliveryPreferences;
  enabled: boolean;
}): NotificationDeliveryPreferences {
  const targetKey = getNotificationTargetKey(input);
  if (!targetKey) return { ...DEFAULT_DELIVERY_PREFS };
  const map = readNotificationDeliveryPrefsMap();
  const current = { ...DEFAULT_DELIVERY_PREFS, ...(map[targetKey] || {}) };
  current[input.key] = input.enabled;
  map[targetKey] = current;
  writeNotificationDeliveryPrefsMap(map);
  return current;
}

export function createBookingStatusNotifications(input: {
  status: 'cancelled' | 'rescheduled';
  referenceCode: string;
  fieldId?: string;
  fieldName: string;
  date: string;
  startTime: string;
  endTime: string;
  customerId?: string;
  customerPhone?: string;
  ownerId?: string;
  ownerPhone?: string;
}): void {
  const typeForCustomer = input.status === 'cancelled' ? 'booking_cancelled_customer' : 'booking_rescheduled_customer';
  const typeForOwner = input.status === 'cancelled' ? 'booking_cancelled_owner' : 'booking_rescheduled_owner';
  const messageCore =
    `${input.fieldName} • ${input.date} ${input.startTime}-${input.endTime} • Ref: ${input.referenceCode}`;

  createNotification({
    userId: input.customerId,
    contactPhone: input.customerPhone,
    type: typeForCustomer,
    title: input.status === 'cancelled' ? 'Rezervasyon İptal Edildi' : 'Rezervasyon Saati Değişti',
    message: messageCore,
    fieldId: input.fieldId,
    date: input.date,
  });

  createNotification({
    userId: input.ownerId,
    contactPhone: input.ownerPhone,
    type: typeForOwner,
    title: input.status === 'cancelled' ? 'Rezervasyon İptali Bildirimi' : 'Rezervasyon Değişim Bildirimi',
    message: messageCore,
    fieldId: input.fieldId,
    date: input.date,
  });
}

export function createBookingRequestNotification(input: {
  referenceCode: string;
  fieldId: string;
  fieldName: string;
  date: string;
  startTime: string;
  endTime: string;
  ownerId?: string;
  ownerPhone?: string;
}): void {
  createNotification({
    userId: input.ownerId,
    contactPhone: input.ownerPhone,
    type: 'booking_request_owner',
    title: `Yeni Rezervasyon Talebi (${input.referenceCode})`,
    message: `${input.fieldName} • ${input.date} ${input.startTime}-${input.endTime} için müşteri talebi geldi.`,
    fieldId: input.fieldId,
    date: input.date,
  });
}

export function createBookingApprovedNotification(input: {
  referenceCode: string;
  fieldId: string;
  fieldName: string;
  date: string;
  startTime: string;
  endTime: string;
  customerId?: string;
  customerPhone?: string;
}): void {
  createNotification({
    userId: input.customerId,
    contactPhone: input.customerPhone,
    type: 'booking_approved_customer',
    title: `Rezervasyon Talebiniz Onaylandı (${input.referenceCode})`,
    message: `${input.fieldName} • ${input.date} ${input.startTime}-${input.endTime} talebiniz onaylandı. Ödeme yapıp tamamlayın.`,
    fieldId: input.fieldId,
    date: input.date,
  });
}

export function createBookingPaidNotifications(input: {
  referenceCode: string;
  fieldId: string;
  fieldName: string;
  date: string;
  startTime: string;
  endTime: string;
  customerId?: string;
  customerPhone?: string;
  ownerId?: string;
  ownerPhone?: string;
}): void {
  const message = `${input.fieldName} • ${input.date} ${input.startTime}-${input.endTime} rezervasyonu kesinleşti.`;
  createNotification({
    userId: input.customerId,
    contactPhone: input.customerPhone,
    type: 'booking_paid_customer',
    title: `Rezervasyon Kesinleşti (${input.referenceCode})`,
    message,
    fieldId: input.fieldId,
    date: input.date,
  });
  createNotification({
    userId: input.ownerId,
    contactPhone: input.ownerPhone,
    type: 'booking_paid_owner',
    title: `Ödeme Tamamlandı (${input.referenceCode})`,
    message,
    fieldId: input.fieldId,
    date: input.date,
  });
}

export function createBookingPaymentProofNotification(input: {
  referenceCode: string;
  fieldId: string;
  fieldName: string;
  date: string;
  startTime: string;
  endTime: string;
  ownerId?: string;
  ownerPhone?: string;
}): void {
  createNotification({
    userId: input.ownerId,
    contactPhone: input.ownerPhone,
    type: 'booking_payment_proof_owner',
    title: `Ödeme Dekontu Gönderildi (${input.referenceCode})`,
    message: `${input.fieldName} • ${input.date} ${input.startTime}-${input.endTime} için müşteri ödeme dekontu gönderdi.`,
    fieldId: input.fieldId,
    date: input.date,
  });
}

export function createBookingPaymentRejectedNotification(input: {
  referenceCode: string;
  fieldId: string;
  fieldName: string;
  date: string;
  startTime: string;
  endTime: string;
  customerId?: string;
  customerPhone?: string;
  reason?: string;
}): void {
  createNotification({
    userId: input.customerId,
    contactPhone: input.customerPhone,
    type: 'booking_payment_rejected_customer',
    title: `Ödeme Red Edildi (${input.referenceCode})`,
    message: `${input.fieldName} • ${input.date} ${input.startTime}-${input.endTime} için ödeme kanıtı reddedildi.${input.reason ? ` Sebep: ${input.reason}` : ''}`,
    fieldId: input.fieldId,
    date: input.date,
  });
}

export function createSubscriptionRequestNotification(input: {
  fieldId: string;
  fieldName: string;
  ownerId?: string;
  ownerPhone?: string;
  dayLabel: string;
  timeLabel: string;
}): void {
  createNotification({
    userId: input.ownerId,
    contactPhone: input.ownerPhone,
    type: 'subscription_request_owner',
    title: 'Yeni Abonelik Talebi',
    message: `${input.fieldName} için ${input.dayLabel} ${input.timeLabel} saatinde abonelik talebi geldi.`,
    fieldId: input.fieldId,
  });
}

export function createSubscriptionApprovedNotification(input: {
  fieldId: string;
  fieldName: string;
  customerId?: string;
  customerPhone?: string;
  dayLabel: string;
  timeLabel: string;
}): void {
  createNotification({
    userId: input.customerId,
    contactPhone: input.customerPhone,
    type: 'subscription_approved_customer',
    title: 'Abonelik Talebiniz Onaylandı',
    message: `${input.fieldName} için ${input.dayLabel} ${input.timeLabel} talebiniz onaylandı. Şimdi ödeme yaparak aboneliği aktifleştirin.`,
    fieldId: input.fieldId,
  });
}

export function createSubscriptionActivatedNotifications(input: {
  fieldId: string;
  fieldName: string;
  customerId?: string;
  customerPhone?: string;
  ownerId?: string;
  ownerPhone?: string;
  dayLabel: string;
  timeLabel: string;
}): void {
  const message = `${input.fieldName} için ${input.dayLabel} ${input.timeLabel} aboneliği aktif oldu.`;
  createNotification({
    userId: input.customerId,
    contactPhone: input.customerPhone,
    type: 'subscription_activated_customer',
    title: 'Aboneliğiniz Aktif',
    message,
    fieldId: input.fieldId,
  });
  createNotification({
    userId: input.ownerId,
    contactPhone: input.ownerPhone,
    type: 'subscription_activated_owner',
    title: 'Abonelik Ödemesi Tamamlandı',
    message,
    fieldId: input.fieldId,
  });
}

export function createSubscriptionCancelledNotifications(input: {
  fieldId: string;
  fieldName: string;
  customerId?: string;
  customerPhone?: string;
  ownerId?: string;
  ownerPhone?: string;
  cancelledBy: 'user' | 'owner';
  refundAmount: number;
}): void {
  const message = `${input.fieldName} aboneliği iptal edildi. İade tutarı: ${input.refundAmount}₺.`;
  createNotification({
    userId: input.customerId,
    contactPhone: input.customerPhone,
    type: 'subscription_cancelled_customer',
    title: input.cancelledBy === 'owner' ? 'Saha Sahibi Aboneliği İptal Etti' : 'Aboneliğiniz İptal Edildi',
    message,
    fieldId: input.fieldId,
  });
  createNotification({
    userId: input.ownerId,
    contactPhone: input.ownerPhone,
    type: 'subscription_cancelled_owner',
    title: input.cancelledBy === 'user' ? 'Kullanıcı Aboneliği İptal Etti' : 'Abonelik İptal Edildi',
    message,
    fieldId: input.fieldId,
  });
}

export interface BookingMessageLinks {
  referenceCode: string;
  customerWhatsappUrl?: string;
  customerSmsUrl?: string;
  ownerWhatsappUrl?: string;
  ownerSmsUrl?: string;
}

function buildBookingMessages(input: {
  booking: Booking;
  ownerMessageNote?: string;
}): { referenceCode: string; customerMessage: string; ownerMessage: string; start: string; end: string } {
  const { booking, ownerMessageNote } = input;
  const referenceCode = booking.id.toUpperCase();
  const start = formatHour(booking.hour);
  const end = formatHour(booking.hour + booking.duration);

  const customerMessage =
    `Rezervasyon onaylandı.\n` +
    `Ref: ${referenceCode}\n` +
    `Saha: ${booking.fieldName}\n` +
    `Tarih/Saat: ${booking.date} ${start}-${end}\n` +
    `Takım: ${booking.teamName}`;

  const ownerMessage =
    `Yeni rezervasyon alındı.\n` +
    `Ref: ${referenceCode}\n` +
    `Saha: ${booking.fieldName}\n` +
    `Tarih/Saat: ${booking.date} ${start}-${end}\n` +
    `Takım: ${booking.teamName}\n` +
    `İletişim: ${booking.contactName} - ${booking.contactPhone}` +
    (ownerMessageNote?.trim() ? `\nMüşteri Mesajı: ${ownerMessageNote.trim()}` : '');

  return { referenceCode, customerMessage, ownerMessage, start, end };
}

export function createBookingMessageLinks(input: {
  booking: Booking;
  ownerPhone?: string;
  ownerMessageNote?: string;
}): BookingMessageLinks {
  const { booking, ownerPhone, ownerMessageNote } = input;
  const { referenceCode, customerMessage, ownerMessage } = buildBookingMessages({ booking, ownerMessageNote });

  const customerWpPhone = normalizeTrPhoneForWhatsapp(booking.contactPhone);
  const ownerWpPhone = normalizeTrPhoneForWhatsapp(ownerPhone);
  const customerSmsPhone = normalizePhone(booking.contactPhone);
  const ownerSmsPhone = normalizePhone(ownerPhone);

  const customerWhatsappUrl = customerWpPhone
    ? `https://wa.me/${customerWpPhone}?text=${encodeURIComponent(customerMessage)}`
    : undefined;
  const ownerWhatsappUrl = ownerWpPhone
    ? `https://wa.me/${ownerWpPhone}?text=${encodeURIComponent(ownerMessage)}`
    : undefined;

  const customerSmsUrl = customerSmsPhone
    ? `sms:${customerSmsPhone}?&body=${encodeURIComponent(customerMessage)}`
    : undefined;
  const ownerSmsUrl = ownerSmsPhone
    ? `sms:${ownerSmsPhone}?&body=${encodeURIComponent(ownerMessage)}`
    : undefined;

  return {
    referenceCode,
    customerWhatsappUrl,
    customerSmsUrl,
    ownerWhatsappUrl,
    ownerSmsUrl,
  };
}

export function dispatchBookingConfirmationMessages(input: {
  booking: Booking;
  ownerId?: string;
  ownerPhone?: string;
  ownerName?: string;
  ownerMessageNote?: string;
}): BookingMessageLinks {
  const { booking, ownerId, ownerPhone, ownerName, ownerMessageNote } = input;
  const { referenceCode, start, end } = buildBookingMessages({ booking, ownerMessageNote });
  const links = createBookingMessageLinks({ booking, ownerPhone, ownerMessageNote });

  createNotification({
    userId: booking.customerId,
    contactPhone: booking.contactPhone,
    type: 'booking_confirmed_customer',
    title: `Rezervasyon Onayı (${referenceCode})`,
    message: `${booking.fieldName} için ${booking.date} ${start}-${end} rezervasyonunuz onaylandı.`,
    fieldId: booking.fieldId,
    bookingId: booking.id,
    date: booking.date,
    hour: booking.hour,
  });

  if (ownerPhone) {
    createNotification({
      userId: ownerId,
      contactPhone: ownerPhone,
      type: 'booking_confirmed_owner',
      title: `Yeni Rezervasyon (${referenceCode})`,
      message: `${ownerName || 'Saha sahibi'}: ${booking.fieldName} için ${booking.date} ${start}-${end} saatinde yeni rezervasyon alındı.`,
      fieldId: booking.fieldId,
      bookingId: booking.id,
      date: booking.date,
      hour: booking.hour,
    });
  }

  return links;
}

export function getReminderPlansForCustomer(userId?: string, phone?: string): ReminderPlan[] {
  if (!userId && !phone) return [];
  const plans = safeRead<ReminderPlan[]>(REMINDER_KEY, []);
  return plans
    .filter((item) => isSameTarget({ userId, contactPhone: phone }, item))
    .sort((a, b) => new Date(a.triggerAt).getTime() - new Date(b.triggerAt).getTime());
}

export function registerBookingReminders(booking: Booking) {
  if (!booking.customerId && !booking.contactPhone) return;

  const startAt = parseBookingDateHour(booking.date, booking.hour);
  const now = new Date();
  const list = safeRead<ReminderPlan[]>(REMINDER_KEY, []);

  const reminderConfig: Array<{ type: ReminderType; hoursBefore: number }> = [
    { type: 'reminder_24h', hoursBefore: 24 },
    { type: 'reminder_2h', hoursBefore: 2 },
  ];

  for (const cfg of reminderConfig) {
    const triggerAt = new Date(startAt.getTime() - cfg.hoursBefore * 60 * 60 * 1000);
    if (triggerAt <= now) continue;

    const exists = list.some(
      (item) => item.bookingId === booking.id && item.type === cfg.type
    );
    if (exists) continue;

    list.push({
      id: `reminder-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      bookingId: booking.id,
      userId: booking.customerId,
      contactPhone: booking.contactPhone,
      fieldId: booking.fieldId,
      fieldName: booking.fieldName,
      date: booking.date,
      hour: booking.hour,
      triggerAt: triggerAt.toISOString(),
      type: cfg.type,
    });
  }

  safeWrite(REMINDER_KEY, list);
}

export function processDueReminders(now: Date = new Date()): number {
  const list = safeRead<ReminderPlan[]>(REMINDER_KEY, []);
  let sentCount = 0;

  const next = list.map((item) => {
    if (item.sentAt) return item;
    if (new Date(item.triggerAt) > now) return item;

    const typeText = item.type === 'reminder_24h' ? '24 saat kaldı' : '2 saat kaldı';
    const start = formatHour(item.hour);
    const end = formatHour(item.hour + 1);

    createNotification({
      userId: item.userId,
      contactPhone: item.contactPhone,
      type: item.type,
      title: `Maç Hatırlatması (${typeText})`,
      message: `${item.fieldName} için ${item.date} ${start}-${end} rezervasyonunuz var.`,
      fieldId: item.fieldId,
      bookingId: item.bookingId,
      date: item.date,
      hour: item.hour,
    });

    sentCount += 1;
    return {
      ...item,
      sentAt: now.toISOString(),
    };
  });

  safeWrite(REMINDER_KEY, next);
  return sentCount;
}

export function getNotificationsForCustomer(userId?: string, phone?: string): AppNotification[] {
  if (!userId && !phone) return [];
  const list = safeRead<AppNotification[]>(NOTIFICATION_KEY, []);
  return list
    .filter((item) => isSameTarget({ userId, contactPhone: phone }, item))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

export function markAllNotificationsRead(userId?: string, phone?: string): number {
  if (!userId && !phone) return 0;
  const list = safeRead<AppNotification[]>(NOTIFICATION_KEY, []);
  let changed = 0;
  const next = list.map((item) => {
    if (!isSameTarget({ userId, contactPhone: phone }, item)) return item;
    if (item.read) return item;
    changed += 1;
    return { ...item, read: true };
  });
  safeWrite(NOTIFICATION_KEY, next);
  return changed;
}

export function joinWaitlist(input: {
  fieldId: string;
  fieldName: string;
  date: string;
  hour: number;
  label: string;
  customerId?: string;
  contactName: string;
  contactPhone: string;
}): { ok: true; entry: WaitlistEntry } | { ok: false; error: string } {
  const contactName = input.contactName.trim();
  const contactPhone = input.contactPhone.trim();
  if (!contactName || !contactPhone) {
    return { ok: false, error: 'Bekleme listesi için ad ve telefon zorunludur.' };
  }

  const list = safeRead<WaitlistEntry[]>(WAITLIST_KEY, []);
  const exists = list.some((item) =>
    item.fieldId === input.fieldId &&
    item.date === input.date &&
    Math.abs(item.hour - input.hour) < 0.001 &&
    (item.status === 'active' || item.status === 'notified') &&
    isSameTarget(
      { userId: input.customerId, contactPhone },
      { userId: item.customerId, contactPhone: item.contactPhone }
    )
  );

  if (exists) {
    return { ok: false, error: 'Bu saat için zaten bekleme listesindesiniz.' };
  }

  const entry: WaitlistEntry = {
    id: `wait-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    fieldId: input.fieldId,
    fieldName: input.fieldName,
    date: input.date,
    hour: input.hour,
    label: input.label,
    customerId: input.customerId,
    contactName,
    contactPhone,
    createdAt: new Date().toISOString(),
    status: 'active',
  };

  safeWrite(WAITLIST_KEY, [entry, ...list]);

  createNotification({
    userId: entry.customerId,
    contactPhone: entry.contactPhone,
    type: 'waitlist_joined',
    title: 'Bekleme Listesine Eklendiniz',
    message: `${entry.fieldName} ${entry.date} ${entry.label} için sıraya alındınız.`,
    fieldId: entry.fieldId,
    date: entry.date,
    hour: entry.hour,
  });

  return { ok: true, entry };
}

export function getWaitlistEntriesForCustomer(userId?: string, phone?: string): WaitlistEntry[] {
  if (!userId && !phone) return [];
  const list = safeRead<WaitlistEntry[]>(WAITLIST_KEY, []);
  return list
    .filter((item) => isSameTarget({ userId, contactPhone: phone }, { userId: item.customerId, contactPhone: item.contactPhone }))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

export function notifyWaitlistForOpenedSlot(input: {
  fieldId: string;
  fieldName: string;
  date: string;
  hour: number;
}): WaitlistEntry | null {
  const list = safeRead<WaitlistEntry[]>(WAITLIST_KEY, []);
  const candidates = list
    .filter((item) =>
      item.fieldId === input.fieldId &&
      item.date === input.date &&
      Math.abs(item.hour - input.hour) < 0.001 &&
      item.status === 'active'
    )
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const winner = candidates[0];
  if (!winner) return null;

  const now = new Date().toISOString();
  const next = list.map((item) =>
    item.id === winner.id
      ? { ...item, status: 'notified' as const, notifiedAt: now }
      : item
  );
  safeWrite(WAITLIST_KEY, next);

  createNotification({
    userId: winner.customerId,
    contactPhone: winner.contactPhone,
    type: 'waitlist_slot_opened',
    title: 'Beklediğiniz Saat Boşaldı',
    message: `${input.fieldName} ${input.date} ${winner.label} saati açıldı. Hızlıca rezervasyon yapabilirsiniz.`,
    fieldId: input.fieldId,
    date: input.date,
    hour: input.hour,
  });

  return { ...winner, status: 'notified', notifiedAt: now };
}

export function markWaitlistConvertedByBooking(booking: Booking): void {
  const list = safeRead<WaitlistEntry[]>(WAITLIST_KEY, []);
  const candidates = list
    .filter((item) =>
      item.fieldId === booking.fieldId &&
      item.date === booking.date &&
      Math.abs(item.hour - booking.hour) < 0.001 &&
      (item.status === 'active' || item.status === 'notified') &&
      isSameTarget(
        { userId: booking.customerId, contactPhone: booking.contactPhone },
        { userId: item.customerId, contactPhone: item.contactPhone }
      )
    )
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const winner = candidates[0];
  if (!winner) return;

  const now = new Date().toISOString();
  const next = list.map((item) =>
    item.id === winner.id
      ? { ...item, status: 'converted' as const, convertedAt: now, bookingId: booking.id }
      : item
  );
  safeWrite(WAITLIST_KEY, next);
}
